import { createFileRoute } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { useCallback, useMemo } from 'react';

import {
  OperationArchivesContainer,
  SearchSchema,
} from '@/features/operation-archives';

import { useItOperationPackages } from '../packages/-query/api';

import { useItOperationCoreArchives } from './-query';

export const Route = createFileRoute(
  '/_staff/it-operation-core/_root/archives/',
)({
  component: ItOperationCoreArchives,
  validateSearch: zodValidator(SearchSchema),
});

function ItOperationCoreArchives() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();

  const archivesQuery = useItOperationCoreArchives({
    page: search.pageIndex + 1,
    pageSize: search.pageSize,
    textSearch: search.textSearch,
    operationPackageId: search.operationPackageId,
    anchorDateFrom: search.anchorDateFrom,
    anchorDateTo: search.anchorDateTo,
    orderBy: search.orderBy,
    orderDirection: search.orderDirection,
  });

  const { data: operationPackagesData, isPending: isOperationPackagesPending } =
    useItOperationPackages();

  const packageOptionsQuery = useMemo(
    () => ({
      data: operationPackagesData
        ? [
            ...operationPackagesData.apps.map((item) => ({
              id: item.id,
              name: item.name,
            })),
            ...operationPackagesData.devices.map((item) => ({
              id: item.id,
              name: item.name,
            })),
            ...operationPackagesData.sharedDrive.map((item) => ({
              id: item.id,
              name: item.name,
            })),
            ...operationPackagesData.network.map((item) => ({
              id: item.id,
              name: item.name,
            })),
          ]
        : undefined,
      isPending: isOperationPackagesPending,
    }),
    [operationPackagesData, isOperationPackagesPending],
  );

  /**
   * アーカイブ行クリック時の遷移処理
   * - デバイス棚卸しパッケージ: packageInstanceIdからrequestIdを取得してデータ画面に遷移
   * - その他のパッケージ: オペレーションインスタンス詳細画面に遷移
   */
  const handleArchiveRowClick = useCallback(
    (row: { packageType: string; patternId: string; instanceId: string }) => {
      if (row.packageType === 'DeviceInventory') {
        // デバイス棚卸しパッケージの場合はデータ画面に遷移
        // packageInstanceIdからrequestIdを自動取得してURLを更新
        navigate({
          to: '/it-operation-core/device-inventory/data',
          search: {
            patternId: row.patternId,
            packageInstanceId: row.instanceId,
          },
        });
      } else {
        // その他のパッケージはインスタンス詳細画面に遷移
        navigate({
          to: `/it-operation-core/operation-instances/${row.instanceId}/detail`,
        });
      }
    },
    [navigate],
  );

  return (
    <OperationArchivesContainer
      searchParams={search}
      onNavigate={navigate}
      archivesQuery={archivesQuery}
      packageOptionsQuery={packageOptionsQuery}
      onRowClick={handleArchiveRowClick}
      hideTargetForPackageTypes={[
        'BulkAppUserCreation',
        'BulkAppUserDeactivation',
        'DeviceInventoryLabelCreation',
      ]}
    />
  );
}
